/* A month's GST, produced from the books rather than typed into a portal.
 *
 * india_compliance computes all of it; what it does not do is put a month in
 * one place, so an accountant opens six reports, exports six spreadsheets,
 * and reconciles them by hand. This is the month, and the download is the
 * same figures in one file.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useErp } from '@jackdesk/erp-kit'
import type { ErpError, GstMonth } from '@jackdesk/erp-kit'
import { Failed, Loading, useToast } from '@jackdesk/shell'

/** The month just gone, which is the one people file. */
function lastMonth() {
  const end = new Date(); end.setDate(0)
  const to = end.toISOString().slice(0, 10)
  return { from: `${to.slice(0, 8)}01`, to }
}

export function Gst() {
  const erp = useErp()
  const toast = useToast()
  const navigate = useNavigate()
  const start = lastMonth()
  const [company, setCompany] = useState('')
  const [from, setFrom] = useState(start.from)
  const [to, setTo] = useState(start.to)
  const [data, setData] = useState<GstMonth | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    erp.list('Company', { fields: ['name'], limit: 1 })
      .then((r) => setCompany(String(r.rows[0]?.name ?? '')))
      .catch(() => setCompany(''))
  }, [erp])

  const load = useCallback(() => {
    if (!company) return
    setFailed(null); setData(null)
    erp.gstMonth(company, from, to).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, company, from, to])
  useEffect(load, [load])

  const groups = ['GSTR-1', 'GSTR-3B', 'Checks', 'Portal']

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 940 }}>
      <div>
        <h1>GST for the month</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          What was sold, what was bought, and the summary that gets filed — worked out
          from your own invoices. Nothing here is typed in, and nothing is recalculated,
          so what you download is what gets filed.
        </p>
      </div>

      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="field" style={{ maxWidth: 180 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>From</span>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field" style={{ maxWidth: 180 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>To</span>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <span className="grow" />
        <button className="btn btn-primary" disabled={busy || !company}
                onClick={async () => {
                  setBusy(true)
                  try { await erp.downloadGstPack(company, from, to) }
                  catch (e) { toast('error', (e as ErpError).message) }
                  finally { setBusy(false) }
                }}>
          {busy ? 'Building…' : 'Download the working papers'}
        </button>
      </div>

      {failed ? <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
        : !data ? <Loading rows={6} label="Working out the month" />
        : (
          <div style={{ display: 'grid', gap: 14 }}>
            {groups.map((group) => {
              const parts = data.parts.filter((p) => p.for === group)
              if (!parts.length) return null
              return (
                <section key={group} className="card" data-part={`gst-${group}`}>
                  <div className="row-between" style={{ padding: '10px 12px 0' }}>
                    <strong className="small">{group}</strong>
                  </div>
                  <table className="tbl">
                    <thead><tr><th>What it is</th><th className="num">Lines</th><th></th></tr></thead>
                    <tbody>
                      {parts.map((p) => (
                        <tr key={p.key} style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/report/${encodeURIComponent(p.report)}`)}>
                          <td>{p.report}<div className="small muted">{p.why}</div></td>
                          <td className="num">
                            {p.not_available ? '—' : p.rows.toLocaleString('en-IN')}
                          </td>
                          <td className="small muted">
                            {p.not_available
                              ? p.not_available
                              : p.rows ? 'open it' : 'nothing in this month'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )
            })}
            <p className="small muted" style={{ margin: 0 }}>{data.note}</p>
          </div>
        )}
    </div>
  )
}
